import { Badge, BadgeAction } from '@/components/ui/badge'
import { InputGroup } from '@/components/ui/input-group'
import { Label } from '@/components/ui/label'
import { Typography } from '@/components/ui/typography'
import { useInputBarcodeScanner } from '@/hooks/useBarcodeScanner'
import { useIsReadOnly } from '@/lib/casl'
import { useFieldContext } from '@/lib/form-contexts'
import { cn } from '@/lib/utils'
import { X } from 'lucide-react'
import { useId, useRef, useState } from 'react'
import FieldInfo from './FieldInfo'

export type BarcodeListInputProps = {
  id?: string
  label?: string
  className?: string
}

/** Retail barcodes (8 to 14 digits) separated by spaces, as typed into the old one-barcode field. */
const RETAIL_BARCODE_LIST = /^\d{8,14}(\s+\d{8,14})+$/

/**
 * Commas and new lines separate barcodes. A space belongs to the code (FS 102), except between
 * retail barcodes. The barcodes migration splits saved values the same way.
 */
function splitBarcodes(text: string): string[] {
  return text
    .split(/[,\n]/)
    .map((part) => part.trim())
    .flatMap((part) => (RETAIL_BARCODE_LIST.test(part) ? part.split(/\s+/) : [part]))
    .filter((barcode) => barcode !== '')
}

function addBarcodes(current: readonly string[], barcodes: readonly string[]): string[] {
  return [...new Set([...current, ...barcodes])]
}

/**
 * A list of barcodes for one product option. Each scan adds a barcode and keeps the field ready for
 * the next one; typed barcodes are added with Enter or when the field loses focus.
 */
export default function BarcodeListInput({
  id,
  label = 'Barcodes',
  className,
}: BarcodeListInputProps) {
  const field = useFieldContext<string[]>()
  const { isReadOnly } = useIsReadOnly()
  const randomId = useId()
  const inputId = id || `${randomId}-${field.name}`
  const [draft, setDraft] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const barcodes = field.state.value

  const commitBarcodes = (text: string) => {
    const typedBarcodes = splitBarcodes(text)

    setDraft('')
    if (typedBarcodes.length > 0) {
      field.handleChange((current) => addBarcodes(current, typedBarcodes))
    }
  }

  // The field holds only barcodes, so a finished scan adds everything in it. A scanner that pauses
  // mid-code can't leave half a barcode behind. The input is read directly because a scan that ends
  // on a pause reports after its last key has rendered.
  const handleScanKeyDown = useInputBarcodeScanner(() =>
    commitBarcodes(inputRef.current?.value ?? '')
  )

  const removeBarcode = (barcode: string) => {
    field.handleChange((current) => current.filter((currentBarcode) => currentBarcode !== barcode))
  }

  return (
    <div className={cn('flex w-full flex-col gap-y-2', className)}>
      <Label htmlFor={inputId}>{label}</Label>

      {/* Barcodes wrap beside the field once two fit per line, so a long list stays short. */}
      <div className="@container/barcodes flex flex-wrap items-center gap-2">
        {isReadOnly ? null : (
          <InputGroup
            inputRef={inputRef}
            id={inputId}
            placeholder="Scan or type a barcode"
            value={draft}
            autoComplete="off"
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              handleScanKeyDown(event)

              if (!event.defaultPrevented && event.key === 'Enter') {
                event.preventDefault()
                commitBarcodes(draft)
              }
            }}
            onBlur={() => {
              commitBarcodes(draft)
              field.handleBlur()
            }}
            className="w-56 max-w-full"
            inputClassName="w-full"
          />
        )}

        {barcodes.length > 0 ? (
          <ul
            aria-label={`Added ${label.toLowerCase()}`}
            className="flex min-w-0 basis-full flex-wrap gap-1.5 @[36rem]/barcodes:flex-1 @[36rem]/barcodes:basis-0"
          >
            {barcodes.map((barcode) => (
              <li key={barcode}>
                <Badge variant="secondary" className="gap-1.5 pr-1">
                  <span className="max-w-44 truncate">{barcode}</span>
                  {isReadOnly ? null : (
                    <BadgeAction
                      aria-label={`Remove barcode ${barcode}`}
                      onClick={() => removeBarcode(barcode)}
                    >
                      <X className="size-3" />
                    </BadgeAction>
                  )}
                </Badge>
              </li>
            ))}
          </ul>
        ) : isReadOnly ? (
          <Typography variant="label" tone="muted">
            No barcodes added yet.
          </Typography>
        ) : null}
      </div>

      <FieldInfo />
    </div>
  )
}
